import './ProviderSearch.css';
import './workQueue/workQueue.css';

import { HiddenFilters, NONE, describeHidden, shown, tallyOptions } from './workQueue/queueFilters';
import { NoteHistory, OwnerCell, WorkItemBadges, WorkItemForm } from './workQueue/WorkItemForm';
import React, { useEffect, useState } from 'react';
import {
  WorkItemAnnotation,
  WorkItemNote,
  WorkItemSubmission,
  exclusionLabel,
  rollForward,
  statusLabel,
  statusRank,
  teamLabel,
  useWorkQueueLists
} from './workQueue/workQueueLists';

import ColumnFilter from './workQueue/ColumnFilter';
import GuideBanner from './GuideBanner';
import GuideLink from './GuideLink';
import axios from 'axios';
import { useDataEnvironment } from '../dataEnvironment';
import { useNavigate } from 'react-router-dom';

type DepartmentNote = WorkItemNote;

interface ActionableVisitType {
  visitTypeId: string | null;
  visitTypeName: string | null;
  comboCount: number;
  providerCount: number;
  buildProviders: number;
  deepLabel: string | null;
  deepCode: string | null;
  sampleEpicMessage: string | null;
}

interface DepartmentRow {
  departmentId: string;
  departmentName: string | null;
  providerCount: number;
  comboCount: number;
  passCount: number;
  reasonProviderCounts: Record<string, number>;
  deepProviderCounts: Record<string, number>;
  deptFlagProviderCount: number;
  deepBuildProviderCount: number;
  /** Roster providers whose own published locations carry this department. */
  publishedFlagProviderCount: number;
  publishedBuildProviderCount: number;
  reachable: boolean;
  excluded: boolean;
  providersBehindFix: number;
  actionable: boolean;
  opportunity: string | null;
  annotation: WorkItemAnnotation | null;
  actionableVisitTypes: ActionableVisitType[];
}

type SortField = 'providersBehindFix' | 'departmentName' | 'providerCount' | 'publishedFlagProviderCount' | 'publishedBuildProviderCount' | 'workStatus' | 'owner' | 'closedAt';
type SortDirection = 'asc' | 'desc';

type DepartmentView = 'open' | 'excluded' | 'history' | 'all';

const DEPARTMENT_VIEWS: { key: DepartmentView; label: string; title: string }[] = [
  {
    key: 'open',
    label: 'Open work',
    title: 'Actionable departments - published in Symplr, not never-bookable, with something to fix - whose work item is neither closed nor excluded.'
  },
  { key: 'excluded', label: 'Excluded', title: 'Items with an exclusion in force today. They return to Open work when the end date passes.' },
  {
    key: 'history',
    label: 'History',
    title: 'Closed items: verified Resolved by a person, or Auto-Resolved when the weekly workbook showed nothing left to fix. A later workbook that finds providers held back again reopens it.'
  },
  { key: 'all', label: 'All departments', title: 'Every department in the snapshot, including operating rooms and other departments no patient can reach.' }
];

type SectionKey = 'reach' | 'workStatus' | 'exclusion' | 'team' | 'assignedTo';

const SECTION_TITLES: Record<SectionKey, string> = {
  reach: 'Reach',
  workStatus: 'Work status',
  exclusion: 'Exclusion',
  team: 'Team',
  assignedTo: 'Assigned to'
};

const PAGE_SIZE = 40;
const COLUMN_COUNT = 9;

/**
 * The department work queue.
 *
 * Separate from the provider queue because it answers a different question: not
 * "what is wrong with this provider" but "what would one Epic change fix for a
 * whole clinic". Defaults to actionable departments only - the unfiltered list
 * is led by operating rooms and cath labs, which have the most providers behind
 * a single flag and should never be open-schedulable.
 */
const AuditDepartments: React.FC = () => {
  const lists = useWorkQueueLists();
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [snapshotDate, setSnapshotDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Arriving from a provider's "Dept config" badge: that provider's departments, whatever their reach.
  const [idFilter, setIdFilter] = useState<string[]>(() =>
    (new URLSearchParams(window.location.search).get('ids') || '').split(',').map((id) => id.trim()).filter(Boolean)
  );
  const [view, setView] = useState<DepartmentView>(idFilter.length > 0 ? 'all' : 'open');
  const [hidden, setHidden] = useState<HiddenFilters>({});
  const [sortField, setSortField] = useState<SortField>('providersBehindFix');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [notesById, setNotesById] = useState<Record<string, DepartmentNote[]>>({});
  const [notesLoading, setNotesLoading] = useState<string | null>(null);
  const environment = useDataEnvironment();
  const navigate = useNavigate();

  useEffect(() => {
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  const loadDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/epic-blocks/departments');
      setDepartments(response.data.departments || []);
      setSnapshotDate(response.data.snapshotDate ?? null);
    } catch (err: any) {
      console.error('Error loading departments:', err);
      setError('Failed to load department rollups.');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = async (departmentId: string) => {
    if (expandedId === departmentId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(departmentId);
    if (notesById[departmentId]) return;

    setNotesLoading(departmentId);
    try {
      const response = await axios.get(`/api/epic-blocks/department-notes/${departmentId}`);
      setNotesById((prev) => ({ ...prev, [departmentId]: response.data.notes || [] }));
    } catch (err) {
      console.error('Error loading department notes:', err);
    } finally {
      setNotesLoading(null);
    }
  };

  /** Saves a note; resolves to an error message, or null once saved. */
  const submitNote = async (department: DepartmentRow, submission: WorkItemSubmission): Promise<string | null> => {
    try {
      const response = await axios.post(`/api/epic-blocks/department-notes/${department.departmentId}`, submission);
      const note: DepartmentNote = response.data.note;
      setNotesById((prev) => ({
        ...prev,
        [department.departmentId]: [note, ...(prev[department.departmentId] || [])]
      }));
      setDepartments((prev) =>
        prev.map((row) =>
          row.departmentId === department.departmentId
            ? { ...row, annotation: rollForward(row.annotation, note, lists, false) }
            : row
        )
      );
      return null;
    } catch (err: any) {
      return err?.response?.data?.error || 'Failed to add note.';
    }
  };

  const assigneeNames = new Map<string, string>();
  departments.forEach((row) => {
    const person = row.annotation?.assignedTo;
    if (person) assigneeNames.set(person.upn, person.displayName);
  });

  const valueOf: Record<SectionKey, (row: DepartmentRow) => string> = {
    reach: (row) =>
      row.excluded
        ? 'Never bookable (excluded)'
        : !row.reachable
          ? 'Not published'
          : row.providersBehindFix === 0 && (row.deptFlagProviderCount > 0 || row.deepBuildProviderCount > 0)
            ? 'Not for these providers'
            : 'Published',
    workStatus: (row) => row.annotation?.status ?? NONE,
    exclusion: (row) => {
      const a = row.annotation;
      if (!a?.exclusionType) return NONE;
      return `${a.excludedFromUnresolved ? 'active' : 'ended'}:${a.exclusionType}`;
    },
    team: (row) => row.annotation?.assignedTeam ?? NONE,
    assignedTo: (row) => row.annotation?.assignedTo?.upn ?? NONE
  };

  const SECTION_OPTIONS: Record<SectionKey, Parameters<typeof tallyOptions>[2]> = {
    reach: {},
    workStatus: {
      label: (v) => statusLabel(lists, v),
      noneLabel: 'No notes yet',
      order: (a, b) => (a === NONE ? 1 : b === NONE ? -1 : statusRank(lists, a) - statusRank(lists, b))
    },
    exclusion: {
      label: (v) => {
        const type = exclusionLabel(lists, v.slice(v.indexOf(':') + 1));
        return v.startsWith('ended:') ? `${type} (ended)` : type;
      },
      noneLabel: 'No exclusion'
    },
    team: { label: (v) => teamLabel(lists, v), noneLabel: 'No team' },
    assignedTo: { label: (v) => assigneeNames.get(v) ?? v, noneLabel: 'Unassigned' }
  };

  const inView = (row: DepartmentRow, which: DepartmentView): boolean => {
    const a = row.annotation;
    switch (which) {
      case 'open':
        return row.actionable && !a?.addressed && !a?.excludedFromUnresolved;
      case 'excluded':
        return !!a?.excludedFromUnresolved;
      case 'history':
        return !!a?.addressed;
      default:
        return true;
    }
  };

  const term = searchTerm.trim().toLowerCase();
  const matches = (row: DepartmentRow, skip: SectionKey | null = null, which: DepartmentView = view): boolean => {
    if (!inView(row, which)) return false;
    if (idFilter.length > 0 && !idFilter.includes(row.departmentId)) return false;
    for (const key of Object.keys(valueOf) as SectionKey[]) {
      if (key !== skip && !shown(hidden[key], valueOf[key](row))) return false;
    }
    if (term) {
      const name = (row.departmentName || '').toLowerCase();
      if (!name.includes(term) && !row.departmentId.includes(term)) return false;
    }
    return true;
  };

  const setSection = (key: SectionKey) => (next: string[]) => {
    setHidden((prev) => ({ ...prev, [key]: next }));
    setCurrentPage(1);
  };

  const filterFor = (label: string, keys: SectionKey[], field?: SortField) => (
    <ColumnFilter
      label={label}
      sections={keys.map((key) => ({
        key,
        title: keys.length > 1 ? SECTION_TITLES[key] : undefined,
        hidden: hidden[key] || [],
        onChange: setSection(key),
        options: tallyOptions(departments.filter((row) => matches(row, key)), valueOf[key], {
          hidden: hidden[key],
          ...SECTION_OPTIONS[key]
        })
      }))}
      sort={field ? {
        direction: sortField === field ? sortDirection : null,
        onSort: (direction) => { setSortField(field); setSortDirection(direction); setCurrentPage(1); }
      } : undefined}
    />
  );

  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'departmentName' || field === 'owner' || field === 'workStatus' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (field: SortField) => (sortField !== field ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓');

  const changeView = (next: DepartmentView) => {
    setView(next);
    setCurrentPage(1);
    if (next === 'history') {
      setSortField('closedAt');
      setSortDirection('desc');
    } else if (sortField === 'closedAt') {
      setSortField('providersBehindFix');
      setSortDirection('desc');
    }
  };

  const filtered = departments.filter((row) => matches(row));

  const pick = (row: DepartmentRow): string | number | null => {
    switch (sortField) {
      case 'departmentName': return row.departmentName || '';
      case 'workStatus': return row.annotation ? statusRank(lists, row.annotation.status) : null;
      case 'owner':
        return row.annotation?.assignedTo?.displayName ??
          (row.annotation?.assignedTeam ? teamLabel(lists, row.annotation.assignedTeam) : null);
      case 'closedAt': return row.annotation?.closedAt ?? null;
      default: return row[sortField];
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const left = pick(a);
    const right = pick(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    const comparison = typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const viewCounts = DEPARTMENT_VIEWS.reduce((acc, { key }) => {
    acc[key] = departments.filter((row) => matches(row, null, key)).length;
    return acc;
  }, {} as Record<DepartmentView, number>);

  const activeSections = (Object.keys(valueOf) as SectionKey[]).filter((key) => (hidden[key]?.length ?? 0) > 0);
  const filtersAreDefault = view === 'open' && !term && activeSections.length === 0 && idFilter.length === 0;
  const resetFilters = () => {
    changeView('open');
    setHidden({});
    setSearchTerm('');
    setIdFilter([]);
  };

  const actionableCount = departments.filter((row) => row.actionable).length;
  const flagFixes = departments
    .filter((row) => row.actionable)
    .reduce((total, row) => total + row.publishedFlagProviderCount, 0);
  const buildFixes = departments
    .filter((row) => row.actionable)
    .reduce((total, row) => total + row.publishedBuildProviderCount, 0);

  return (
    <div className="provider-search-container">
      <GuideBanner storageKey="departments" section="departments">
        <strong>Work this page before the provider list.</strong> One Epic change here clears every
        provider behind it, and the ranking already excludes departments no patient can reach.
      </GuideBanner>

      <div className="intro-section">
        <h1>
          Department Work Queue
          <span className="info-tooltip">
            <span className="info-icon">ⓘ</span>
            <span className="tooltip-text">
              Departments where a single Epic change would clear several providers at once.
              <br /><br />
              Ranked only among departments published on a Symplr Marketing Master location. Without
              that filter the list is led by operating rooms and cath labs, which have the most
              providers behind one department flag and are not meant to be open-schedulable.
            </span>
          </span>
          <GuideLink section="departments" label="How to work this queue" spaced />
        </h1>
        <p className="subtitle">
          The provider queue asks what is wrong with one provider. This asks what one Epic change
          would fix for a whole clinic.
          {snapshotDate && (
            <> Showing snapshot <strong>{snapshotDate}</strong> — {actionableCount} actionable
              department{actionableCount === 1 ? '' : 's'} covering {flagFixes} department-flag and{' '}
              {buildFixes} visit-type build fixes.</>
          )}
        </p>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Department name or ID..."
            />
          </div>
        </div>
      </div>

        {loading && <p>Loading departments...</p>}
        {error && <p className="error-message">{error}</p>}

        {departments.length > 0 && (
          <div className="results-section">
            <div className="wq-views" role="tablist" aria-label="Queue views">
              {DEPARTMENT_VIEWS.map(({ key, label, title }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={view === key}
                  className={`wq-view ${view === key ? 'wq-view-active' : ''}`}
                  title={title}
                  onClick={() => changeView(key)}
                >
                  {label}
                  <span className="wq-view-count">{viewCounts[key]}</span>
                </button>
              ))}
              <GuideLink section="departments-controls" label="Views and filters" spaced />
            </div>

            {(activeSections.length > 0 || term || idFilter.length > 0) && (
              <div className="wq-chips" aria-label="Active filters">
                {idFilter.length > 0 && (
                  <span className="wq-chip" title={idFilter.join(', ')}>
                    One provider&rsquo;s departments ({idFilter.length})
                    <button type="button" aria-label="Clear department list" onClick={() => setIdFilter([])}>×</button>
                  </span>
                )}
                {term && (
                  <span className="wq-chip">
                    Search: &ldquo;{searchTerm}&rdquo;
                    <button type="button" aria-label="Clear search" onClick={() => setSearchTerm('')}>×</button>
                  </span>
                )}
                {activeSections.map((key) => {
                  const options = tallyOptions(departments.filter((row) => matches(row, key)), valueOf[key], { hidden: hidden[key], ...SECTION_OPTIONS[key] });
                  const label = `${SECTION_TITLES[key]}: ${describeHidden(hidden[key], options)}`;
                  return (
                    <span key={key} className="wq-chip">
                      {label}
                      <button type="button" aria-label={`Remove ${label}`} onClick={() => setSection(key)([])}>×</button>
                    </span>
                  );
                })}
                {!filtersAreDefault && (
                  <button type="button" className="link-button" onClick={resetFilters}>Reset to defaults</button>
                )}
              </div>
            )}

            <div className="results-header">
              <p className="results-count">
                Showing {sorted.length} of {departments.length} department{departments.length !== 1 ? 's' : ''}
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </p>
            </div>

            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('departmentName')}>Department{sortIcon('departmentName')}</th>
                    <th className="sortable" onClick={() => handleSort('providersBehindFix')} title="Providers a single Epic change here would unblock.">
                      One fix unblocks{sortIcon('providersBehindFix')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('publishedFlagProviderCount')} title="Roster providers held back by the department Open Scheduling flag (DEP 53200), counting only those whose own published locations carry this department.">
                      Dept flag{sortIcon('publishedFlagProviderCount')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('publishedBuildProviderCount')} title="Roster providers hitting a Level 2 visit-type build problem here, counting only those whose own published locations carry this department.">
                      Visit-type build{sortIcon('publishedBuildProviderCount')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('providerCount')}>Providers{sortIcon('providerCount')}</th>
                    <th>
                      <span className="wq-th">Reach{filterFor('Reach', ['reach'])}</span>
                    </th>
                    <th className="sortable" onClick={() => handleSort(view === 'history' ? 'closedAt' : 'workStatus')}>
                      <span className="wq-th">
                        Work item{sortIcon(view === 'history' ? 'closedAt' : 'workStatus')}
                        {filterFor('Work item', ['workStatus', 'exclusion'], 'workStatus')}
                      </span>
                    </th>
                    <th className="sortable" onClick={() => handleSort('owner')}>
                      <span className="wq-th">Owner{sortIcon('owner')}{filterFor('Owner', ['team', 'assignedTo'], 'owner')}</span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && sorted.length === 0 && (
                    <tr>
                      <td colSpan={COLUMN_COUNT} className="wq-muted">
                        No departments match this view and these filters.{' '}
                        {!filtersAreDefault && (
                          <button type="button" className="link-button" onClick={resetFilters}>Reset to defaults</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {visible.map((department) => (
                    <React.Fragment key={department.departmentId}>
                      <tr className={expandedId === department.departmentId ? 'wq-row-expanded' : undefined}>
                        <td>
                          <strong>{department.departmentName || 'Unknown'}</strong>
                          <br />
                          <small>{department.departmentId}</small>
                        </td>
                        <td>
                          <span style={{ fontWeight: 700, fontSize: '15px' }}>
                            {department.providersBehindFix || '–'}
                          </span>
                        </td>
                        <td>{department.publishedFlagProviderCount || '–'}</td>
                        <td>{department.publishedBuildProviderCount || '–'}</td>
                        <td>{department.providerCount}</td>
                        <td>
                          {department.excluded ? (
                            <span style={{ color: '#6b7480', fontStyle: 'italic' }} title="Never bookable by design.">
                              Excluded
                            </span>
                          ) : department.reachable && department.providersBehindFix === 0 &&
                            (department.deptFlagProviderCount > 0 || department.deepBuildProviderCount > 0) ? (
                            <span
                              style={{ color: '#b26a00', fontWeight: 600 }}
                              title="Published on some location, but not on the locations of the providers blocked here, so a fix would not surface them."
                            >
                              Not for these providers
                            </span>
                          ) : department.reachable ? (
                            <span style={{ color: '#107C10', fontWeight: 600 }}>Published</span>
                          ) : (
                            <span
                              style={{ color: '#b26a00', fontWeight: 600 }}
                              title="No Symplr Marketing Master location publishes this department, so an Epic fix alone would not surface anyone."
                            >
                              Not published
                            </span>
                          )}
                        </td>
                        <td>
                          <WorkItemBadges annotation={department.annotation} lists={lists} />
                        </td>
                        <td>
                          <OwnerCell annotation={department.annotation} lists={lists} />
                        </td>
                        <td>
                          <button
                            onClick={() => toggleExpanded(department.departmentId)}
                            className="btn btn-sm btn-secondary"
                          >
                            {expandedId === department.departmentId ? 'Hide' : 'Details & Notes'}
                            {department.annotation?.noteCount ? ` (${department.annotation.noteCount})` : ''}
                          </button>
                        </td>
                      </tr>
                      {expandedId === department.departmentId && (
                        <tr className="wq-detail-row">
                          <td colSpan={COLUMN_COUNT}>
                            {department.opportunity && (
                              <p style={{ marginTop: 0 }}>
                                <strong>Opportunity:</strong> {department.opportunity}
                              </p>
                            )}

                            {department.actionableVisitTypes.length > 0 ? (
                              <>
                                <strong>Visit types to name in the ticket</strong>
                                <GuideLink section="departments-ticket" label="What a ticket needs" spaced />
                                <table className="results-table" style={{ marginTop: '0.5rem' }}>
                                  <thead>
                                    <tr>
                                      <th>Visit Type</th>
                                      <th>Providers</th>
                                      <th>Deep Finding</th>
                                      <th>Epic&apos;s message</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {department.actionableVisitTypes.map((visitType, idx) => (
                                      <tr key={`${department.departmentId}-vt-${idx}`}>
                                        <td>
                                          {visitType.visitTypeName || '–'}
                                          <br />
                                          <small>{visitType.visitTypeId || ''}</small>
                                        </td>
                                        <td>{visitType.buildProviders}</td>
                                        <td>{visitType.deepLabel || '–'}</td>
                                        <td><small>{visitType.sampleEpicMessage || 'N/A'}</small></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>
                            ) : (
                              <p style={{ color: '#6b7480' }}>
                                No visit-type build problems here; the fix is the department flag itself.
                              </p>
                            )}

                            <div className="wq-panel" style={{ marginTop: '1rem' }}>
                              <h4>
                                Department work queue
                                {department.annotation && (
                                  <span style={{ fontWeight: 400, color: '#6b7480' }}>
                                    {' '}- currently {statusLabel(lists, department.annotation.status)}
                                  </span>
                                )}
                                <GuideLink section="departments-statuses" label="Statuses" spaced />
                              </h4>
                              <WorkItemForm
                                key={department.departmentId}
                                annotation={department.annotation}
                                lists={lists}
                                placeholder="Ticket number, who owns it, what was requested..."
                                onSubmit={(submission) => submitNote(department, submission)}
                              />
                              <NoteHistory
                                notes={notesById[department.departmentId]}
                                loading={notesLoading === department.departmentId}
                                lists={lists}
                                emptyText="No notes yet. Be the first to annotate this department."
                                context={(note) =>
                                  note.providersBehindFix !== null && note.providersBehindFix !== undefined
                                    ? `(${note.providersBehindFix} behind the fix)`
                                    : null
                                }
                              />
                            </div>

                            <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => navigate(`/provider-queue?department=${encodeURIComponent(department.departmentId)}`)}
                              >
                                See the {department.providerCount} individual provider
                                {department.providerCount === 1 ? '' : 's'} in this department
                              </button>
                            </p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination" style={{ marginTop: '1rem' }}>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span style={{ margin: '0 0.75rem' }}>Page {currentPage} of {totalPages}</span>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default AuditDepartments;
